import { useCallback, useMemo } from 'react';
import { Box, Flex, Switch } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
    SETTINGS_BORDER_COLOR,
    SettingsCard,
    SettingsNumberField,
    SettingsRow,
} from '../../../../components/settings/primitives';
import {
    applyLkqPricingRuleChange,
    groupLkqPricingRules,
    removeLkqPricingRule,
} from '../../../../utils/lkqPricingRows';
import PricingTableSection from './pricing/PricingTableSection';
import type { PricingTableTab } from './pricing/PricingTableSection';
import {
    PRICING_TABLES,
    percentageOf,
    pricingEntriesOf,
    pricingEntryFactory,
    pricingRowPath,
    pricingUpdate,
    type PricingEntry,
    type PricingTableSpec,
} from './pricing/pricingTables';
import {
    decimalToWholePercent,
    useInvoicingDraft,
    usePricingRowNames,
    wholePercentToDecimal,
} from './shared';
import type { InvoicingSectionProps } from './types';

const SECTION_ID = 'invoicing-markups';

type DefaultMarkupField = 'defaultLkqMarkupPercentage' | 'defaultSubletMarkupPercentage';
type PreferListPriceField =
    'preferInvoiceListPriceOverLkqMarkup' | 'preferInvoiceListPriceOverSubletMarkup';

/** The defaults each tab shows above its rows. */
const DEFAULTS_BY_TAB: Record<
    string,
    {
        percentField: DefaultMarkupField;
        percentLabel: string;
        percentDescription: string;
        preferField: PreferListPriceField;
        preferDescription: string;
    }
> = {
    parts: {
        percentField: 'defaultLkqMarkupPercentage',
        percentLabel: 'Default parts-specific markup',
        percentDescription:
            'Default markup applied when the list price or cost is not specified on an invoice for used and aftermarket lines. It does not apply to OEM, glass, or reman lines.',
        preferField: 'preferInvoiceListPriceOverLkqMarkup',
        preferDescription:
            'Prefer the list price extracted from the invoice image over a list price calculated from the markups below.',
    },
    sublet: {
        percentField: 'defaultSubletMarkupPercentage',
        percentLabel: 'Default sublet markup',
        percentDescription:
            'Default markup applied when the list price or cost is not specified on an invoice for sublet lines.',
        preferField: 'preferInvoiceListPriceOverSubletMarkup',
        preferDescription:
            'Prefer the list price extracted from the invoice image over a list price calculated from the sublet markups below.',
    },
};

const MarkupsSection = ({ data, organizationStates }: InvoicingSectionProps) => {
    const { invoicing, updateInvoicing, isSaving } = useInvoicingDraft(data);
    const navigate = useNavigate();

    const serverRows = useMemo(
        () => [...(data.lkqMarkups ?? []), ...(data.subletMarkups ?? [])],
        [data.lkqMarkups, data.subletMarkups],
    );
    const { withNames, rememberNames } = usePricingRowNames(serverRows);

    /** One table's draft entries, with the display names re-attached. */
    const entriesOf = useCallback(
        (spec: PricingTableSpec): PricingEntry[] => withNames(pricingEntriesOf(spec, invoicing)),
        [withNames, invoicing],
    );

    const lkqSpec = PRICING_TABLES[SECTION_ID][0];
    const subletSpec = PRICING_TABLES[SECTION_ID][1];

    /** Build a tab from its spec: the copy and the writes are all it differs by. */
    const buildTab = (spec: PricingTableSpec): PricingTableTab => {
        const entries = entriesOf(spec);
        const readPercentage = percentageOf(spec);
        const defaults = DEFAULTS_BY_TAB[spec.tabId];
        return {
            id: spec.tabId,
            label: spec.tabLabel,
            rows: groupLkqPricingRules(entries, readPercentage),
            percentColumnLabel: spec.percentColumnLabel,
            emptyText: spec.emptyText,
            addButtonText: 'Add Row',
            rowNoun: spec.rowNoun,
            // Adding and editing are the same full-screen rule editor,
            // which owns its own save; 'new' is the add case.
            onAdd: () => navigate(pricingRowPath(SECTION_ID, spec.tabId, 'new')),
            onEditRow: (rule) => navigate(pricingRowPath(SECTION_ID, spec.tabId, rule.ruleKey)),
            onSaveRow: (rule, values) => {
                // The update payload carries ids only, so a newly picked
                // vendor's name is kept beside the draft until the next refetch.
                rememberNames([
                    {
                        insuranceCompanyId: rule.insuranceCompanyId,
                        insuranceCompanyName: rule.insuranceCompanyName,
                        supplierId: values.supplierId,
                        supplierName: values.supplierName,
                    },
                ]);
                updateInvoicing(
                    pricingUpdate(
                        spec,
                        applyLkqPricingRuleChange(
                            entries,
                            rule,
                            values,
                            readPercentage,
                            pricingEntryFactory(spec),
                        ),
                    ),
                );
            },
            onRemoveRow: (ruleKey) =>
                updateInvoicing(
                    pricingUpdate(spec, removeLkqPricingRule(entries, ruleKey, readPercentage)),
                ),
            defaults: (
                // Side by side: two short settings read as one strip above the
                // rows instead of stacking and pushing the table down.
                <SettingsCard dividers={false}>
                    {/* Each half owns an equal share of the width, so a long
                        description wraps inside its own column rather than
                        pushing the other setting off the row. */}
                    <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                        <Box flex="1" minW={0}>
                            <SettingsRow
                                label={defaults.percentLabel}
                                description={defaults.percentDescription}
                                control={
                                    <SettingsNumberField
                                        value={
                                            invoicing[defaults.percentField] != null
                                                ? decimalToWholePercent(
                                                      invoicing[defaults.percentField]!,
                                                  )
                                                : null
                                        }
                                        onChange={(next) =>
                                            updateInvoicing({
                                                [defaults.percentField]:
                                                    next == null
                                                        ? undefined
                                                        : wholePercentToDecimal(next),
                                            })
                                        }
                                        min={0}
                                        max={100}
                                        width="90px"
                                        unit="%"
                                        ariaLabel={defaults.percentLabel}
                                        isDisabled={isSaving}
                                    />
                                }
                            />
                        </Box>
                        {/* Divider between the halves, which becomes the row
                            divider once they stack. */}
                        <Box
                            flex="0 0 auto"
                            mx={{ lg: 6 }}
                            borderColor={SETTINGS_BORDER_COLOR}
                            borderLeftWidth={{ base: 0, lg: '1px' }}
                            borderTopWidth={{ base: '1px', lg: 0 }}
                        />
                        <Box flex="1" minW={0}>
                            <SettingsRow
                                label="Prefer invoice list price over markup"
                                description={defaults.preferDescription}
                                control={
                                    <Switch
                                        size="lg"
                                        isChecked={invoicing[defaults.preferField] === true}
                                        onChange={(e) =>
                                            updateInvoicing({
                                                [defaults.preferField]: e.target.checked,
                                            })
                                        }
                                        colorScheme="partbay-blue"
                                        isDisabled={isSaving}
                                    />
                                }
                            />
                        </Box>
                    </Flex>
                </SettingsCard>
            ),
        };
    };

    return (
        <PricingTableSection
            organizationStates={organizationStates}
            isSaving={isSaving}
            tabs={[buildTab(lkqSpec), buildTab(subletSpec)]}
        />
    );
};

export default MarkupsSection;
